import { useTranslation } from 'next-i18next'
import { useRef, useState } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Divider from '~/bootcamp/Activities/components/Divider'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import FractionsActivityWrapper from '~/bootcamp/Activities/Fractions/components/FractionsActivityWrapper'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'

import useFractionImproperToMixed from './useFractionImproperToMixed'

function FractionImproperToMixed({
  answerState,
  actionButtonsFC,
  autofocus = true,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  difficultyLevel,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const { question, formValid, inputs, error, onInputChange, handleCheckAnswer, goToNextQuestion } =
    useFractionImproperToMixed({
      difficultyLevel,
      onAnswerStateChange,
      onNextQuestionClick,
    })
  const [inputKey, setInputKey] = useState<string>(uuid())

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  // `answer` (the mixed-number solution) stays out.
  useEmitInstance(
    onInstanceChange,
    question ? `\\frac{${question.numerator}}{${question.denominator}} = \\square \\frac{\\square}{\\square}` : null,
  )

  const handleGoToNextQuestion = (): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }

  const onEnterPress = (): void => {
    if (formValid) {
      hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
    }
  }

  return (
    <ActivityWrapper>
      <FractionsActivityWrapper>
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <span className="text-2xl">{question?.numerator}</span>
            <Divider />
            <span className="text-2xl">{question?.denominator}</span>
          </div>
          <Equals />
          <div className="flex items-center space-x-2">
            <Input
              aria-label={t('ariaLabelButtons.insertValue')}
              autoFocus={autofocus}
              isError={error?.quotient}
              key={`${inputKey}-first`}
              maxDigits={3}
              onChange={(value) => onInputChange('quotient', value)}
              onEnter={onEnterPress}
              ref={inputRef}
              value={`${inputs?.quotient || ''}`}
            />
            <div className="flex flex-col items-center space-y-2">
              <Input
                aria-label={t('ariaLabelButtons.insertNumerator')}
                isError={error?.numerator}
                key={`${inputKey}-second`}
                maxDigits={3}
                onChange={(value) => onInputChange('numerator', value)}
                onEnter={onEnterPress}
                value={`${inputs?.numerator || ''}`}
              />
              <Divider />
              <Input
                aria-label={t('ariaLabelButtons.insertDenominator')}
                isError={error?.denominator}
                key={`${inputKey}-third`}
                maxDigits={3}
                onChange={(value) => onInputChange('denominator', value)}
                onEnter={onEnterPress}
                value={`${inputs?.denominator || ''}`}
              />
            </div>
          </div>
        </div>
      </FractionsActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange(),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default FractionImproperToMixed
